import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data";
import { MaskLine } from "@/components/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.8]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Misty mountain hills seen through bamboo leaves at Vistara"
          className="w-full h-full object-cover"
        />
      </motion.div>

      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-charcoal"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/30" />

      <motion.div
        style={{ y: textY }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="text-cream/80 text-xs md:text-sm uppercase tracking-[0.35em] mb-5 md:mb-8"
        >
          Boutique mountain retreat · Arku Valley
        </motion.p>

        <h1 className="font-serif text-cream leading-[0.86] tracking-tight text-[22vw] md:text-[16vw] lg:text-[13rem]">
          <MaskLine delay={0.35}>Vistara</MaskLine>
        </h1>

        <div className="mt-6 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.75, ease: [0.76, 0, 0.24, 1] }}
              className="font-serif italic text-cream text-2xl md:text-4xl max-w-md"
            >
              Wake up above the clouds.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="flex items-center gap-3 text-cream/70"
          >
            <span className="text-xs uppercase tracking-[0.25em]">Scroll to wander</span>
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
              <ArrowDown size={18} strokeWidth={1.5} />
            </motion.span>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
